import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { AdminApiService } from '../services/api'
import { AdminUser } from '../types/admin'
import CassetteLoader from '../components/CassetteLoader'
import { 
  ArrowLeftIcon,
  UserIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ChartBarIcon,
  CogIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

export function UserDetail() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const [user, setUser] = useState<AdminUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState<Partial<AdminUser>>({})
  const [deleting, setDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    if (userId) {
      loadUser()
    }
  }, [userId])

  const loadUser = async () => {
    try {
      setLoading(true)
      const userData = await AdminApiService.getUser(userId!)
      setUser(userData)
      setEditForm(userData)
    } catch (err: any) {
      toast.error('Failed to load user details')
      console.error('Error loading user:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!user || !userId) return

    try {
      await AdminApiService.updateUser(userId, editForm)
      setUser({ ...user, ...editForm })
      setEditing(false)
      toast.success('User updated successfully')
    } catch (err: any) {
      toast.error('Failed to update user')
      console.error('Error updating user:', err)
    }
  }

  const handleDelete = async () => {
    if (!user || !userId) return

    try {
      setDeleting(true)
      await AdminApiService.deleteUser(userId)
      toast.success('User deleted successfully')
      navigate('/users') // Navigate back to users list
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete user'
      toast.error(errorMessage)
      console.error('Error deleting user:', err)
    } finally {
      setDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const handleCancel = () => {
    setEditForm(user!)
    setEditing(false)
  }

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === '0001-01-01T00:00:00Z') {
      return '—'
    }
    return new Date(dateString).toLocaleDateString()
  }

  const getTierBadge = (tier: string | undefined) => {
    const safeTier = tier || 'starter'
    const styles = {
      starter: 'bg-gray-100 text-gray-800',
      artist: 'bg-blue-100 text-blue-800',
      studio: 'bg-green-100 text-green-800'
    }
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[safeTier as keyof typeof styles] || styles.starter}`}>
        {safeTier.charAt(0).toUpperCase() + safeTier.slice(1)}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CassetteLoader />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">User not found</h3>
        <p className="mt-1 text-sm text-gray-500">The user you're looking for doesn't exist.</p>
        <div className="mt-6">
          <button
            onClick={() => navigate('/users')}
            className="btn btn-primary"
          >
            Back to Users
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-4">
            <button
              onClick={() => navigate('/users')}
              className="btn btn-ghost btn-sm hover:bg-gray-100"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Users
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-semibold text-lg">
                {(user.displayName || user.email).charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {user.displayName || user.email}
                </h1>
                <p className="text-sm text-gray-500">{user.email}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            {editing ? (
              <>
                <button
                  onClick={handleCancel}
                  className="btn btn-ghost btn-sm"
                >
                  <XMarkIcon className="h-4 w-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="btn btn-primary btn-sm"
                >
                  <CheckIcon className="h-4 w-4" />
                  Save Changes
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setEditing(true)}
                  className="btn btn-primary btn-sm"
                >
                  <PencilIcon className="h-4 w-4" />
                  Edit User
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="btn btn-danger btn-sm"
                  disabled={deleting}
                >
                  <TrashIcon className="h-4 w-4" />
                  {deleting ? 'Deleting...' : 'Delete User'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* User Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
            <UserIcon className="h-4 w-4 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">User Information</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="label text-gray-700 font-medium">Display Name</label>
            {editing ? (
              <input
                type="text"
                value={editForm.displayName || ''}
                onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                className="input"
                placeholder="Display name"
              />
            ) : (
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-900 font-medium">{user.displayName || 'Not set'}</p>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <label className="label text-gray-700 font-medium">Firebase Email</label>
            {editing ? (
              <input
                type="email"
                value={editForm.email || ''}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="input"
              />
            ) : (
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-900 font-medium">{user.email}</p>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <label className="label text-gray-700 font-medium">Google Drive Email</label>
            {editing ? (
              <input
                type="email"
                value={editForm.gdriveEmail || ''}
                onChange={(e) => setEditForm({ ...editForm, gdriveEmail: e.target.value })}
                className="input"
              />
            ) : (
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-900 font-medium">{user.gdriveEmail || 'Not connected'}</p>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <label className="label text-gray-700 font-medium">Firebase UID</label>
            <div className="p-3 bg-gray-50 rounded-md">
              <p className="text-sm text-gray-500 font-mono">{user.firebaseUid}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
            <CheckIcon className="h-4 w-4 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Subscription Information</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="label text-gray-700 font-medium">Subscription Tier</label>
            {editing ? (
              <select
                value={editForm.subscriptionTier || 'starter'}
                onChange={(e) => setEditForm({ ...editForm, subscriptionTier: e.target.value as any })}
                className="input"
              >
                <option value="starter">Starter</option>
                <option value="artist">Artist</option>
                <option value="studio">Studio</option>
              </select>
            ) : (
              <div className="p-3 bg-gray-50 rounded-md">
                {getTierBadge(user.subscriptionTier)}
              </div>
            )}
          </div>
          <div>
            <label className="label">Premium Until</label>
            {editing ? (
              <input
                type="datetime-local"
                value={editForm.premiumUntil ? new Date(editForm.premiumUntil).toISOString().slice(0, 16) : ''}
                onChange={(e) => setEditForm({ ...editForm, premiumUntil: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                className="input"
              />
            ) : (
              <p className="text-sm text-gray-900">{formatDate(user.premiumUntil || '')}</p>
            )}
          </div>
          <div>
            <label className="label">Stripe Customer ID</label>
            <p className="text-sm text-gray-500 font-mono">{user.stripeCustomerId || 'Not set'}</p>
          </div>
          <div>
            <label className="label">Active Promo Code</label>
            <p className="text-sm text-gray-900">{user.activePromoCode || 'None'}</p>
          </div>
        </div>
      </div>

      {/* UTM Tracking */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
            <ChartBarIcon className="h-4 w-4 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">UTM Tracking</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="label">UTM Source</label>
            {editing ? (
              <input
                type="text"
                value={editForm.utmSource || ''}
                onChange={(e) => setEditForm({ ...editForm, utmSource: e.target.value })}
                className="input"
              />
            ) : (
              <p className="text-sm text-gray-900">{user.utmSource || 'Not set'}</p>
            )}
          </div>
          <div>
            <label className="label">UTM Medium</label>
            {editing ? (
              <input
                type="text"
                value={editForm.utmMedium || ''}
                onChange={(e) => setEditForm({ ...editForm, utmMedium: e.target.value })}
                className="input"
              />
            ) : (
              <p className="text-sm text-gray-900">{user.utmMedium || 'Not set'}</p>
            )}
          </div>
          <div>
            <label className="label">UTM Campaign</label>
            {editing ? (
              <input
                type="text"
                value={editForm.utmCampaign || ''}
                onChange={(e) => setEditForm({ ...editForm, utmCampaign: e.target.value })}
                className="input"
              />
            ) : (
              <p className="text-sm text-gray-900">{user.utmCampaign || 'Not set'}</p>
            )}
          </div>
          <div>
            <label className="label">UTM Term</label>
            {editing ? (
              <input
                type="text"
                value={editForm.utmTerm || ''}
                onChange={(e) => setEditForm({ ...editForm, utmTerm: e.target.value })}
                className="input"
              />
            ) : (
              <p className="text-sm text-gray-900">{user.utmTerm || 'Not set'}</p>
            )}
          </div>
          <div>
            <label className="label">UTM Content</label>
            {editing ? (
              <input
                type="text"
                value={editForm.utmContent || ''}
                onChange={(e) => setEditForm({ ...editForm, utmContent: e.target.value })}
                className="input"
              />
            ) : (
              <p className="text-sm text-gray-900">{user.utmContent || 'Not set'}</p>
            )}
          </div>
          <div>
            <label className="label">Landing Page</label>
            {editing ? (
              <input
                type="text"
                value={editForm.landingPage || ''}
                onChange={(e) => setEditForm({ ...editForm, landingPage: e.target.value })}
                className="input"
              />
            ) : (
              <p className="text-sm text-gray-900">{user.landingPage || 'Not set'}</p>
            )}
          </div>
        </div>
      </div>

      {/* System Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
            <CogIcon className="h-4 w-4 text-gray-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">System Information</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="label">Created</label>
            <p className="text-sm text-gray-900">{formatDate(user.createdAt || user.firstSeenAt || '')}</p>
          </div>
          <div>
            <label className="label">Last Updated</label>
            <p className="text-sm text-gray-900">{formatDate(user.lastUpdatedAt || user.createdAt || user.firstSeenAt || '')}</p>
          </div>
          <div>
            <label className="label">Google Drive Token</label>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              user.hasGoogleDriveToken ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {user.hasGoogleDriveToken ? 'Connected' : 'Not Connected'}
            </span>
          </div>
          <div>
            <label className="label">Token Expiry</label>
            <p className="text-sm text-gray-900">{formatDate(user.googleDriveTokenExpiry || '')}</p>
          </div>
          <div>
            <label className="label">Root Folder</label>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              user.hasGDriveRootFolder ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {user.hasGDriveRootFolder ? 'Created' : 'Not Created'}
            </span>
          </div>
        </div>
        {user.gDriveRootLink && (
          <div className="mt-4">
            <label className="label">Google Drive Root Link</label>
            <a
              href={user.gDriveRootLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-900 text-sm break-all"
            >
              {user.gDriveRootLink}
            </a>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete User</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete user <strong>{user.email}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-ghost"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="btn btn-danger"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

